
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PlusCircle } from 'lucide-react';
import { Process } from '@/types/database';
import { useAuth } from '@/hooks/use-auth';

async function fetchProcesses() {
  const { data, error } = await supabase
    .from('processes')
    .select('*')
    .order('updated_at', { ascending: false });
    
  if (error) {
    console.error('Error fetching processes:', error);
    throw new Error(error.message);
  }
  return data || [];
}

const ProcessesPage = () => {
  const { user } = useAuth();

  const { data: processes, isLoading, error } = useQuery<Process[]>({
    queryKey: ['processes'],
    queryFn: fetchProcesses,
    enabled: !!user,
  });

  return (
    <div className="container mx-auto p-4 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Processos</h1>
        <Link to="/processes/new">
          <Button>
            <PlusCircle className="mr-2 h-4 w-4" />
            Novo Processo
          </Button>
        </Link>
      </div>

      {isLoading && <p>Carregando processos...</p>}
      {error && <p className="text-red-500">Ocorreu um erro ao carregar os processos. Por favor, tente novamente mais tarde.</p>}

      {!isLoading && !error && processes?.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <h2 className="text-xl font-semibold">Nenhum processo encontrado</h2>
          <p className="text-muted-foreground mt-2">Comece criando seu primeiro processo.</p>
          <Link to="/processes/new" className="mt-4 inline-block">
            <Button>
              <PlusCircle className="mr-2 h-4 w-4" />
              Criar Primeiro Processo
            </Button>
          </Link>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {processes?.map((process) => (
          <Link to={`/processes/${process.id}`} key={process.id}>
            <Card className="hover:shadow-lg transition-shadow h-full">
              <CardHeader>
                <CardTitle>{process.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Atualizado em: {new Date(process.updated_at).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ProcessesPage;
